import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Text } from '../components/UI';
import { IUser, useLogin, useCustomerChecklist, IPermission } from '../services';


export interface WithAuth {
    user: IUser;
}

export type PermissionLevels = 'USER' | 'ADMIN' | 'TECHNICIAN' | 'SUPERUSER' | 'EVERYBODY' | 'SUPERADMIN';

export function withAuth<T extends object>(WrappedComponent: React.FunctionComponent<T & WithAuth>, permissionRequirement: PermissionLevels) {
    
    const WithAuth = (props: T) => {
        const [user] = useLogin();
        const navigate = useNavigate();
        const location = useLocation();
        const [,, checklistKeyFunctions] = useCustomerChecklist();

        React.useEffect(() => {
            if (user?.brand_id) {
                checklistKeyFunctions.checkPageVisits(location.pathname, user.brand_id);
            }
        }, [location]);

        if (!user) {
            navigate(`/login?ref=${encodeURIComponent(location.pathname)}`);
            return null;
        }

        const returnFunc = () => {
            return (
                <WrappedComponent
                    {...props}
                    {...{ user } as WithAuth}
                />
            )
        }

        if (!user.email_verified) {
            return <div className='text-center flex flex-col gap-y-4'>
                <Text className="!text-3xl"> Email Not Verified </Text>
                <Text> Please Verify Your Email Address In Order To Access The Wallmates Dashboard. </Text>
            </div>
        }

        // Optional-chained: permission_id is `number | IPermission` and is only the
        // expanded object on the login response. A user whose stored payload
        // predates that join, or whose permissions row is missing, previously
        // crashed this guard rather than being denied.
        const userPermission = (user.permissions?.permission_id as IPermission | undefined)?.permission;
        if (permissionRequirement === 'USER' && (
            userPermission === 'USER' || userPermission === 'ADMIN' || userPermission === 'SUPERUSER' || userPermission === 'SUPERADMIN'
        )) {
            return returnFunc();
        }

        if (permissionRequirement === 'ADMIN' && (
            userPermission === 'ADMIN' || userPermission === 'SUPERADMIN'
        )) {
            return returnFunc();
        }

        if (permissionRequirement === 'TECHNICIAN' && (
            userPermission === 'TECHNICIAN' || userPermission === 'SUPERUSER' || userPermission === 'SUPERADMIN'
        )) {
            return returnFunc();
        }

        if (permissionRequirement === 'SUPERUSER' && (
            userPermission === 'SUPERUSER' || userPermission === 'SUPERADMIN'
        )) {
            return returnFunc();
        }

        if (permissionRequirement === 'EVERYBODY' && (
            userPermission === 'ADMIN' || userPermission === 'SUPERUSER' || userPermission === 'TECHNICIAN' || userPermission === 'USER' || userPermission === 'SUPERADMIN'
        )) {
            return returnFunc();
        }

        if (permissionRequirement === 'SUPERADMIN' && (
            userPermission === 'SUPERADMIN'
        )) {
            return returnFunc();
        }

        return <div className='text-center flex flex-col gap-y-4 p-20'>
            <Text className="!text-3xl"> No Permission </Text>
            <Text> You have reached a page you do not have sufficient permissions to view. Please reach out to an administrator of your account to get permission if you need to see this page. </Text>
        </div>
    }

    (WithAuth as any).displayName = `withAuth(${WrappedComponent.displayName || WrappedComponent.name})`;

    return WithAuth;
}